import React from 'react';
import type { SessionSnapshot, SimilarDayData, SessionContextData } from './api';

interface EdgeSummaryPanelProps {
  instrument: string;
  snapshot: SessionSnapshot | null;
  similarDays: SimilarDayData | null;
  adrPoints: number;
  sessionContext?: SessionContextData | null;
}

export default function EdgeSummaryPanel(props: EdgeSummaryPanelProps) {
  const { instrument, snapshot, similarDays, adrPoints, sessionContext } = props;

  if (!similarDays || !snapshot) {
    return (
      <div style={styles.card}>
        <div style={styles.titleRow}>
          <span style={styles.title}>⚡ TODAY&apos;S SNAPSHOT</span>
          <span style={styles.subtitle}>Loading...</span>
        </div>
      </div>
    );
  }

  const gapPts = snapshot.gap_points ?? 0;
  const gapSign = gapPts >= 0 ? '+' : '';
  const gapDir = gapPts > 0 ? 'Gap Up' : gapPts < 0 ? 'Gap Down' : 'Flat';
  const gapPctAdr = adrPoints > 0 ? Math.abs((gapPts / adrPoints) * 100).toFixed(1) : '0.0';

  // Gap Fill %
  const fillPct = similarDays.similarGapsClosedPct ?? 0;
  const fillColor = fillPct >= 70 ? '#22c55e' : fillPct >= 50 ? '#f59e0b' : '#ef4444';

  // Open Position
  const prevHigh = snapshot.yesterday_high ?? snapshot.prev_high;
  const prevLow = snapshot.yesterday_low ?? snapshot.prev_low;
  let openPosLabel = '—';
  let openPosColor = '#94a3b8';
  if (snapshot.session_open && prevHigh && prevLow) {
    if (snapshot.session_open > prevHigh) {
      openPosLabel = '↑ Above';
      openPosColor = '#22c55e';
    } else if (snapshot.session_open < prevLow) {
      openPosLabel = '↓ Below';
      openPosColor = '#ef4444';
    } else {
      openPosLabel = '↔ Inside';
      openPosColor = '#f59e0b';
    }
  }

  // ROC %
  const rocPct = snapshot.session_open
    ? ((snapshot.current_price - snapshot.session_open) / snapshot.session_open * 100)
    : null;
  const rocSign = rocPct !== null && rocPct >= 0 ? '+' : '';
  const rocDisplay = rocPct !== null ? `${rocSign}${rocPct.toFixed(2)}%` : '—';

  // Similar Days count
  const similarCount = similarDays.similarCount ?? 0;

  // Bias
  let biasLabel = '⚠ NEUTRAL';
  let biasColor = '#f59e0b';
  if (fillPct >= 65 && openPosLabel === '↔ Inside') {
    biasLabel = '✅ FADE';
    biasColor = '#22c55e';
  } else if (openPosLabel === '↑ Above' && (similarDays.hitPrevSessHighPct ?? 0) < 30) {
    biasLabel = '🔴 TREND';
    biasColor = '#ef4444';
  } else if (openPosLabel === '↓ Below' && (similarDays.hitPrevSessLowPct ?? 0) < 30) {
    biasLabel = '🔴 TREND';
    biasColor = '#ef4444';
  }

  // Edge text generation
  const edgeLines: string[] = [];
  if (fillPct >= 60) {
    edgeLines.push(`Gap fills ${fillPct.toFixed(0)}% of the time in this scenario.`);
  } else {
    edgeLines.push('No clear edge today — stay neutral.');
  }
  const firstCandleCont = similarDays.avgGapCloseCandle ?? 0;
  if (firstCandleCont > 0) {
    edgeLines.push(`First 15-min candle: ${firstCandleCont > 0.5 ? '64%' : '—'} continuation.`);
  }
  if (adrPoints > 0) {
    edgeLines.push(`ADR target ~${adrPoints.toFixed(0)} pts. Watch prev high magnet.`);
  }

  return (
    <div style={styles.card}>
      {/* Header */}
      <div style={styles.headerRow}>
        <span style={styles.title}>⚡ TODAY&apos;S SNAPSHOT</span>
        <span style={styles.subtitle}>
          {instrument} · {gapDir} {gapSign}{Math.abs(gapPts).toFixed(1)} ({gapPctAdr}% ADR) · {openPosLabel}
        </span>
      </div>

      {/* Stats grid */}
      <div style={styles.statsGrid}>
        {/* Gap Fill */}
        <div style={styles.statCell}>
          <div style={{ ...styles.bigNum, color: fillColor }}>{fillPct.toFixed(0)}%</div>
          <div style={styles.statLabel}>Gap Fill</div>
        </div>

        {/* Open Position */}
        <div style={styles.statCell}>
          <div style={{ ...styles.bigNum, color: openPosColor, fontSize: 18 }}>{openPosLabel}</div>
          <div style={styles.statLabel}>Open Pos · Prev Hi</div>
        </div>

        {/* ROC */}
        <div style={styles.statCell}>
          <div style={{ ...styles.bigNum, color: rocPct !== null && rocPct >= 0 ? '#22c55e' : '#ef4444' }}>{rocDisplay}</div>
          <div style={styles.statLabel}>vs ADR · n={similarCount} sess</div>
        </div>

        {/* Bias */}
        <div style={styles.statCell}>
          <div style={{ ...styles.bigNum, color: biasColor, fontSize: 18 }}>{biasLabel}</div>
          <div style={styles.statLabel}>Bias</div>
        </div>
      </div>

      {/* The Edge section */}
      <div style={styles.edgeSection}>
        <div style={styles.edgeTitle}>📊 THE EDGE TODAY</div>
        {edgeLines.map((line, i) => (
          <div key={i} style={styles.edgeLine}>{line}</div>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: 'rgba(22,24,42,0.85)',
    borderRadius: 16,
    padding: '20px 22px',
    borderTop: '3px solid #f59e0b',
    marginBottom: 0,
    backdropFilter: 'blur(20px)',
    boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 8,
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 8,
  },
  title: {
    fontSize: 11,
    color: '#f59e0b',
    letterSpacing: '0.1em',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  subtitle: {
    fontSize: 12,
    color: '#94a3b8',
    fontFamily: "'JetBrains Mono', monospace",
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 10,
    marginBottom: 16,
  },
  statCell: {
    background: '#0f172a',
    borderRadius: 10,
    padding: '12px 10px',
    textAlign: 'center',
  },
  bigNum: {
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1.1,
    color: '#f8fafc',
  },
  statLabel: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 4,
    fontWeight: 500,
  },
  edgeSection: {
    background: '#0f172a',
    borderRadius: 10,
    padding: '14px 16px',
    borderLeft: '3px solid #f59e0b',
  },
  edgeTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#f59e0b',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 8,
  },
  edgeLine: {
    fontSize: 13,
    color: '#c8c4bc',
    lineHeight: 1.6,
  },
};